import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, Package, ArrowRight, ChevronDown } from "lucide-react";
import api from "@/lib/api";

const STATUS_META = {
  awaiting_payment:  { label: "Awaiting payment",     tone: "yellow"  },
  payment_submitted: { label: "Payment submitted",    tone: "blue"    },
  payment_verified:  { label: "Payment confirmed",    tone: "green"   },
  preparing:         { label: "Preparing",            tone: "green"   },
  ready:             { label: "Ready",                tone: "green"   },
  shipped:           { label: "Shipped",              tone: "green"   },
  completed:         { label: "Completed",            tone: "emerald" },
  cancelled:         { label: "Cancelled",            tone: "red"     },
};

const FILTERS = [
  { key: "all",       label: "All" },
  { key: "active",    label: "In progress" },
  { key: "completed", label: "Completed" },
  { key: "cancelled", label: "Cancelled" },
];

const ACTIVE_STATUSES = new Set([
  "awaiting_payment", "payment_submitted", "payment_verified",
  "preparing", "ready", "shipped",
]);

export default function MyOrders() {
  const [orders, setOrders] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [params, setParams] = useSearchParams();
  const filter = params.get("filter") || "all";

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/collector-auth/my-orders");
        if (alive) setOrders(r.data.orders || []);
      } catch (e) {
        if (alive) setError("Unable to load your orders. Please try again in a moment.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const filtered = useMemo(() => {
    const list = orders || [];
    if (filter === "active") return list.filter((o) => ACTIVE_STATUSES.has(o.status));
    if (filter === "completed") return list.filter((o) => o.status === "completed");
    if (filter === "cancelled") return list.filter((o) => o.status === "cancelled");
    return list;
  }, [orders, filter]);

  const setFilter = (key) => {
    const p = new URLSearchParams(params);
    if (key === "all") p.delete("filter"); else p.set("filter", key);
    setParams(p, { replace: true });
  };

  return (
    <div className="space-y-6" data-testid="my-orders-page">
      <div>
        <div className="kicker kicker-red">My Orders</div>
        <h1 className="h-display text-3xl md:text-4xl mt-1" data-testid="my-orders-heading">
          Your CollectorUp order history.
        </h1>
        <p className="text-sm text-[var(--smoke)] mt-2 max-w-2xl">
          Every missing-sticker order you&apos;ve placed — plus any guest orders that were linked to your account.
        </p>
      </div>

      {/* Filter chips */}
      <div className="flex flex-wrap gap-2" data-testid="my-orders-filters">
        {FILTERS.map((f) => {
          const active = filter === f.key;
          return (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              data-testid={`my-orders-filter-${f.key}`}
              className={`px-3 py-1.5 rounded-full border-2 text-xs font-bold uppercase tracking-widest transition ${
                active
                  ? "border-[var(--panini-red)] bg-[var(--panini-red)] text-white"
                  : "border-[var(--ink)] bg-white text-[var(--ink)] hover:bg-[var(--panini-yellow-soft)]"
              }`}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="rounded-2xl border-2 border-[var(--ink)] bg-white p-10 grid place-items-center text-[var(--smoke)]" data-testid="my-orders-loading">
          <Loader2 className="animate-spin" size={28} />
        </div>
      ) : error ? (
        <div className="rounded-2xl border-2 border-[var(--panini-red)] bg-red-50 p-6 text-sm text-red-900" data-testid="my-orders-error">
          {error}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState filter={filter} />
      ) : (
        <div className="rounded-2xl border-2 border-[var(--ink)] bg-white overflow-hidden divide-y-2 divide-[var(--stone)]" data-testid="my-orders-list">
          {filtered.map((o) => <OrderRow key={o.public_id} order={o} />)}
        </div>
      )}
    </div>
  );
}

function OrderRow({ order }) {
  const meta = STATUS_META[order.status] || { label: order.status, tone: "gray" };
  const total = order?.pricing_snapshot?.totals?.total ?? 0;
  const stickerCount = order?.pricing_snapshot?.totals?.sticker_count ?? 0;
  return (
    <Link
      to={`/dashboard/orders/${order.public_id}`}
      data-testid={`my-orders-row-${order.public_id}`}
      className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-6 p-5 hover:bg-[var(--cream)] transition"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono font-bold text-[var(--ink)]" data-testid={`my-orders-row-${order.public_id}-id`}>
            {order.public_id}
          </span>
          <StatusPill meta={meta} testid={`my-orders-row-${order.public_id}-status`} />
        </div>
        <div className="text-xs text-[var(--smoke)] mt-1 flex items-center gap-3 flex-wrap">
          <span>{order.submitted_at ? new Date(order.submitted_at).toLocaleDateString() : "—"}</span>
          <span>·</span>
          <span>{stickerCount} sticker{stickerCount === 1 ? "" : "s"}</span>
          {order?.delivery?.code && (
            <>
              <span>·</span>
              <span className="capitalize">{String(order.delivery.code).replace("_", " ")}</span>
            </>
          )}
        </div>
      </div>
      <div className="flex items-center justify-between md:justify-end gap-4">
        <div className="text-right">
          <div className="font-display text-xl text-[var(--ink)]">${Number(total).toFixed(2)}</div>
          <div className="text-[10px] uppercase tracking-widest text-[var(--smoke)] font-bold">Total</div>
        </div>
        <ArrowRight size={18} className="text-[var(--smoke)]" />
      </div>
    </Link>
  );
}

function StatusPill({ meta, testid }) {
  const toneClasses = {
    yellow:  "bg-[var(--panini-yellow)] text-[var(--ink)] border-[var(--ink)]",
    blue:    "bg-blue-50 text-blue-900 border-blue-900",
    green:   "bg-emerald-50 text-emerald-900 border-emerald-900",
    emerald: "bg-emerald-100 text-emerald-900 border-emerald-900",
    red:     "bg-red-50 text-red-900 border-red-900",
    gray:    "bg-neutral-100 text-[var(--ink)] border-[var(--ink)]",
  };
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border font-bold text-[10px] uppercase tracking-widest ${toneClasses[meta.tone]}`}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
      {meta.label}
    </span>
  );
}

function EmptyState({ filter }) {
  const copy = filter === "all"
    ? "You haven't placed any orders yet."
    : `No orders match "${filter}".`;
  return (
    <div className="rounded-2xl border-2 border-dashed border-[var(--ink)] bg-white p-10 text-center" data-testid="my-orders-empty">
      <Package size={36} className="mx-auto text-[var(--smoke)]" />
      <div className="font-display text-xl mt-3">{copy}</div>
      <p className="text-sm text-[var(--smoke)] mt-2 max-w-sm mx-auto">
        Start a new missing-sticker order in minutes — we&apos;ll ship them or hold them for pickup.
      </p>
      <Link to="/order-missing" className="btn-red mt-5 inline-block" data-testid="my-orders-empty-cta">
        Order missing stickers
      </Link>
    </div>
  );
}
